import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../App";
import { toast } from "sonner";

import { DashboardHeader } from "../components/dashboard/DashboardHeader";
import { AdminDashboard } from "../components/dashboard/AdminDashboard";
import { BasicDashboard } from "../components/dashboard/BasicDashboard";
import { BrokerDashboard } from "../components/dashboard/BrokerDashboard";
import { DeveloperDashboard } from "../components/dashboard/DeveloperDashboard";
import { OwnerDashboard } from "../components/dashboard/OwnerDashboard";
import { ActivationGate } from "../components/dashboard/ActivationGate";
import { SharedSections } from "../components/dashboard/SharedSections";
import { DashboardModals } from "../components/dashboard/DashboardModals";
import { useBrokerDashboard } from "../hooks/useBrokerDashboard";
import { useOwnerDashboard } from "../hooks/useOwnerDashboard";
import { useAdminDashboard } from "../hooks/useAdminDashboard";
import { useDashboardClients } from "../hooks/useDashboardClients";
import { usePropertyManagement } from "../hooks/usePropertyManagement";
import { useDevelopmentManagement } from "../hooks/useDevelopmentManagement";
import { useAppointmentManagement } from "../hooks/useAppointmentManagement";
import { useRepresentationAndRequests } from "../hooks/useRepresentationAndRequests";
import { usePendingSales } from "../hooks/usePendingSales";
import { logError } from "../utils/logger";
import {
  getDerivedPaymentStatus,
  requiresActivation,
  getTrialDaysCopy,
} from "../constants/plans";
import { startSubscriptionCheckout } from "../services/subscriptionActivation";

const Dashboard = () => {
  const { user, login, updateUser } = useAuth();
  const navigate = useNavigate();
  const {
    dashboardApi,
    brokerDashboardApi,
    developerDashboardApi,
    ownerDashboardApi,
  } = useDashboardClients();

  // Core state
  const [currentUser, setCurrentUser] = useState(user);
  const [developerProfile, setDeveloperProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Property management
  const {
    properties,
    deletedProperties,
    stats,
    fetchUserProperties,
    fetchDeletedProperties,
    handleDeleteProperty,
    handleRestoreProperty,
    handleToggleActive,
    handleToggleFeatured,
  } = usePropertyManagement(dashboardApi, developerDashboardApi, setLoading);

  // Development management
  const {
    developments,
    deletedDevelopments,
    fetchDevelopments,
    fetchDeletedDevelopments,
    handleDeleteDevelopment,
    handleRestoreDevelopment,
  } = useDevelopmentManagement(dashboardApi, developerDashboardApi);

  // Appointment management
  const {
    appointments,
    editingAppointment,
    setEditingAppointment,
    editForm,
    setEditForm,
    fetchAppointments,
    handleAppointmentStatus,
    openEditAppointment,
    handleEditAppointment,
  } = useAppointmentManagement(dashboardApi, developerDashboardApi);

  // Representation requests, inquiries, my requests
  const {
    representationRequests,
    inquiries,
    myRequests,
    expandedRequest,
    setExpandedRequest,
    requestResponses,
    fetchRepresentationRequests,
    fetchInquiries,
    fetchMyRequests,
    handleApproveRepresentation,
    handleRejectRepresentation,
    loadResponses,
    updateRequestStatus,
    archiveInquiry,
  } = useRepresentationAndRequests(dashboardApi, developerDashboardApi);

  // Pending sales (owner)
  const {
    pendingSales,
    showConfirmSaleModal,
    setShowConfirmSaleModal,
    selectedSaleNotification,
    setSelectedSaleNotification,
    saleConfirmation,
    setSaleConfirmation,
    fetchPendingSales,
    confirmSale,
    rejectSale,
  } = usePendingSales(ownerDashboardApi, fetchUserProperties);
  const {
    brokerProfile,
    managedProperties,
    marketplaceProperties,
    commissions,
    representedDevelopments,
    brokerLeads,
    leadsFunnel,
    showCommissionModal,
    setShowCommissionModal,
    showLeadModal,
    setShowLeadModal,
    newCommission,
    setNewCommission,
    newLead,
    setNewLead,
    fetchAll: fetchAllBroker,
    fetchBrokerLeads,
    fetchCommissions,
    fetchRepresentedDevelopments,
    createLead,
    updateLeadStatus,
    notifyQualifiedLead,
    createCommission,
    updateCommissionStatus,
    deleteCommission,
    reserveUnit,
    releaseUnit,
  } = useBrokerDashboard();

  // Admin state (via hook)
  const {
    adminStats,
    adminUsers,
    fetchAll: fetchAllAdmin,
  } = useAdminDashboard();

  // ====== FETCH FUNCTIONS ======
  const fetchCurrentUser = async () => {
    try {
      const r = await dashboardApi.getCurrentUser();
      if (r.ok) {
        const d = await r.json();
        setCurrentUser(d);
        updateUser(d);
      }
    } catch (e) {
      logError("Dashboard.fetchCurrentUser", e);
    }
  };

  const fetchDeveloperProfile = async () => {
    try {
      const r = await dashboardApi.getDeveloperProfile();
      if (r.ok) setDeveloperProfile(await r.json());
    } catch (e) {
      logError("Dashboard.fetchDeveloperProfile", e);
    }
  };

  // ====== ACTIONS ======
  const handleUpgrade = async () => {
    try {
      await startSubscriptionCheckout(currentUser || user);
    } catch (e) {
      toast.error(e.message || "Error al procesar pago");
    }
  };

  // ====== EFFECTS ======
  useEffect(() => {
    const activeRole = user?.role;
    fetchCurrentUser();
    if (activeRole === "basic" || activeRole === "admin") {
      setLoading(false);
      if (activeRole === "admin") {
        fetchAllAdmin();
      }
      return;
    }
    fetchUserProperties();
    fetchDeletedProperties();
    fetchRepresentationRequests();
    fetchMyRequests();
    fetchInquiries();
    fetchPendingSales();
  }, []);

  useEffect(() => {
    if (user?.role === "broker") {
      fetchAllBroker();
    }
  }, [user]);

  useEffect(() => {
    if (user && user.role === "developer") {
      fetchDeveloperProfile();
      fetchDevelopments();
      fetchDeletedDevelopments();
      fetchAppointments();
    }
  }, [user]);

  useEffect(() => {
    if (user?.role === "admin") {
      fetchAllAdmin();
    }
  }, [user]);

  // developerProfile useEffect intentionally removed — fetchDevelopments and
  // fetchAppointments are already called in the [user] effect for developers.

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get(
      "session_id",
    );
    if (sessionId) {
      toast.info("Verificando pago...");
      dashboardApi
        .getSubscriptionStatus(sessionId)
        .then((r) => r.json())
        .then((d) => {
          if (["paid", "active", "trialing"].includes(d.status)) {
            const message =
              d.status === "trialing"
                ? `Prueba de ${getTrialDaysCopy()} activada!`
                : "Suscripción activada!";
            toast.success(message);
            setTimeout(() => navigate("/dashboard", { replace: true }), 2000);
          }
        })
        .catch(console.error);
    }
  }, [navigate, dashboardApi]);

  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
    }
  }, [user, navigate]);

  // ====== GUARDS ======
  if (!user) {
    return null;
  }
  if (loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const role = currentUser?.role || user?.role;
  const entitlements = currentUser?.entitlements || user?.entitlements;
  const paymentStatus = getDerivedPaymentStatus(currentUser || user);
  const isPendingActivation = requiresActivation(currentUser || user);
  const isBasicUser = role === "basic";
  const isAdminUser = role === "admin";
  const showRoleDashboards =
    !isBasicUser && !isPendingActivation && !isAdminUser;
  const showLimitedSections = !isBasicUser && isPendingActivation && !isAdminUser;

  // ====== RENDER ======
  return (
    <div className="min-h-screen bg-muted overflow-x-hidden">
      <Header
        inquiries={inquiries}
        representationRequests={representationRequests}
        brokerLeads={brokerLeads}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        <DashboardHeader
          user={user}
          currentUser={currentUser}
          stats={stats}
          developerProfile={developerProfile}
          handleUpgrade={handleUpgrade}
          navigate={navigate}
        />

        <BasicDashboard user={currentUser || user} navigate={navigate} />

        <ActivationGate
          user={isPendingActivation ? currentUser || user : null}
          subscriptionInfo={{ status: paymentStatus }}
          handleUpgrade={handleUpgrade}
          navigate={navigate}
        />

        {isAdminUser && (
          <AdminDashboard
            user={currentUser || user}
            currentUser={currentUser}
            adminStats={adminStats}
            adminUsers={adminUsers}
            navigate={navigate}
          />
        )}

        {showRoleDashboards && (
          <DeveloperDashboard
            user={user}
            developments={developments}
            navigate={navigate}
          />
        )}

        {showRoleDashboards && (
          <BrokerDashboard
            user={user}
            brokerLeads={brokerLeads}
            leadsFunnel={leadsFunnel}
            commissions={commissions}
            managedProperties={managedProperties}
            representedDevelopments={representedDevelopments}
            showLeadModal={showLeadModal}
            setShowLeadModal={setShowLeadModal}
            showCommissionModal={showCommissionModal}
            setShowCommissionModal={setShowCommissionModal}
            updateLeadStatus={updateLeadStatus}
            notifyQualifiedLead={notifyQualifiedLead}
            updateCommissionStatus={updateCommissionStatus}
            deleteCommission={deleteCommission}
            navigate={navigate}
          />
        )}

        {showRoleDashboards && (
          <OwnerDashboard
            user={user}
            pendingSales={pendingSales}
            representationRequests={representationRequests}
            setShowConfirmSaleModal={setShowConfirmSaleModal}
            setSelectedSaleNotification={setSelectedSaleNotification}
            setSaleConfirmation={setSaleConfirmation}
            rejectSale={rejectSale}
            handleApproveRepresentation={handleApproveRepresentation}
            handleRejectRepresentation={handleRejectRepresentation}
            propertyCount={properties.length}
            activePropertyCount={
              properties.filter((p) => p.is_active !== false).length
            }
            navigate={navigate}
          />
        )}

        {(showRoleDashboards || showLimitedSections) && (
          <SharedSections
            user={user}
            currentUser={currentUser}
            properties={properties}
            deletedProperties={deletedProperties}
            inquiries={inquiries}
            developments={developments}
            deletedDevelopments={deletedDevelopments}
            appointments={appointments}
            myRequests={myRequests}
            expandedRequest={expandedRequest}
            requestResponses={requestResponses}
            handleDeleteProperty={handleDeleteProperty}
            handleRestoreProperty={handleRestoreProperty}
            handleToggleActive={handleToggleActive}
            handleToggleFeatured={handleToggleFeatured}
            handleDeleteDevelopment={handleDeleteDevelopment}
            handleRestoreDevelopment={handleRestoreDevelopment}
            handleAppointmentStatus={handleAppointmentStatus}
            openEditAppointment={openEditAppointment}
            loadResponses={loadResponses}
            updateRequestStatus={updateRequestStatus}
            setExpandedRequest={setExpandedRequest}
            navigate={navigate}
            archiveInquiry={archiveInquiry}
            limitedMode={showLimitedSections}
          />
        )}

        {showRoleDashboards && (
          <DashboardModals
            user={user}
            showCommissionModal={showCommissionModal}
            setShowCommissionModal={setShowCommissionModal}
            newCommission={newCommission}
            setNewCommission={setNewCommission}
            createCommission={createCommission}
            showLeadModal={showLeadModal}
            setShowLeadModal={setShowLeadModal}
            newLead={newLead}
            setNewLead={setNewLead}
            createLead={createLead}
            marketplaceProperties={marketplaceProperties}
            managedProperties={managedProperties}
            showConfirmSaleModal={showConfirmSaleModal}
            setShowConfirmSaleModal={setShowConfirmSaleModal}
            selectedSaleNotification={selectedSaleNotification}
            saleConfirmation={saleConfirmation}
            setSaleConfirmation={setSaleConfirmation}
            confirmSale={confirmSale}
            brokerProfile={brokerProfile}
            reserveUnit={reserveUnit}
            releaseUnit={releaseUnit}
            editingAppointment={editingAppointment}
            setEditingAppointment={setEditingAppointment}
            editForm={editForm}
            setEditForm={setEditForm}
            handleEditAppointment={handleEditAppointment}
          />
        )}

        {/* WhatsApp Support */}
        {showRoleDashboards &&
          ((entitlements &&
            (entitlements.can_manage_leads_commissions ||
              entitlements.can_create_developments)) ||
            ["broker", "developer"].includes(role)) && (
            <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-1">
                    Soporte Exclusivo Pro
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Acceso directo a nuestro equipo via WhatsApp
                  </p>
                  <a
                    href="https://wa.me/528115639310?text=Hola,%20soy%20usuario%20Pro%20de%20Metal%20Concrete"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium">
                      Contactar por WhatsApp
                    </button>
                  </a>
                </div>
              </div>
            </div>
          )}
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;
